import { BusinessAccount } from "@/types/user";
import { useCallback, useEffect } from "react";
import { create } from "zustand";

interface BusinessState {
  refreshKey: number;
  isBusiness: boolean;
  setIsBusiness: (notif: boolean) => void;
  businessInfo: BusinessAccount | null;
  setBusinessInfo: (business: BusinessAccount | null) => void;
}

export const useBusinessStore = create<BusinessState>((set) => ({
  refreshKey: 0,
  isBusiness: false,
  setIsBusiness: (notif) => set({ isBusiness: notif }),
  businessInfo: null,
  setBusinessInfo: (business) => set({ businessInfo: business }),
}));

const useBusinessInfo = () => {
  const { businessInfo, setBusinessInfo } = useBusinessStore();

  //custom setter that persists to localStorage
  const updateBusinessInfo = useCallback(
    (business: BusinessAccount | null) => {
      if (business) {
        localStorage.setItem("ACTIVE_BUSINESS", JSON.stringify(business));
      } else {
        localStorage.removeItem("ACTIVE_BUSINESS");
      }
      setBusinessInfo(business);
    },
    [setBusinessInfo]
  );

  useEffect(() => {
    // Only restore from localStorage if businessInfo is null/undefined
    if (!businessInfo) {
      const stored = localStorage.getItem("ACTIVE_BUSINESS");
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed) {
            // Use Zustand's setter directly to avoid the custom setter's localStorage write
            // This prevents a potential loop where updateBusinessInfo writes to localStorage,
            // which might trigger this effect again
            setBusinessInfo(parsed);
            localStorage.setItem("ACTIVE_BUSINESS", JSON.stringify(parsed));
          }
        } catch (error) {
          // Invalid JSON in localStorage, remove it
          localStorage.removeItem("ACTIVE_BUSINESS");
        }
      }
    }
    // Only depend on businessInfo - setBusinessInfo is stable from Zustand
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessInfo]);

  return { businessInfo, setBusinessInfo: updateBusinessInfo };
};

export default useBusinessInfo;
